import dayjs from 'dayjs';
import {createContext, useEffect, useState} from 'react';
import {jwtDecode} from 'jwt-decode';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {
  postAnonLogin,
  postAuthCodeSend,
  postAuthenticate,
  generate2faEmailVerificationCode,
  TwoFAEmailVerification,
} from '../api/auth';
import {AnimationLoadingScreenWithProgressBar} from '../components/loading/loadingScreen';
import axios from 'axios';
import {IDENTITY_URL} from '../api/constants';
import {maskEmail} from '../utils/miscellaneous';
const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({children}) => {
  //this effet should alway on top
  // it will check hit urls if it is one of the define ones it will  clear storage so everything is clean on initial load if user close tab directly
  useEffect(() => {
    const handleOnLoad = () => {
      // Define the routes where you want to clear the storage

      const routesToClearStorage = ['/', '/start', '/atpllanding', '/doktorlanding', '/hellohealth-landing'];

      // Loop through the routes and check if the current pathname matches any of them
      if (routesToClearStorage.includes(window.location.pathname)) {
        console.log('Matching route hit, clearing storage.');
        // Clear both localStorage and sessionStorage
        localStorage.clear();
        sessionStorage.clear();
      }
    };
    handleOnLoad();
  }, []);

  const [isSuccessFullyVerify, setIsSuccessfullyVerify] = useState(false);
  const [userActive, setUserActive] = useState(() => {
    const storedTokens = localStorage.getItem('authTokens');
    const sessionId = localStorage.getItem('session-id');

    if (storedTokens) {
      return jwtDecode(JSON.parse(localStorage.getItem('authTokens')).access_token);
    }
    if (sessionId) {
      return {sessionId, type: 'org'};
    }
    return null;
  });

  const [authTokens, setAuthTokens] = useState(
    localStorage.getItem('authTokens') !== null ? JSON.parse(localStorage.getItem('authTokens')) : null,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [canBeAuthenticated, setCanBeAuthenticated] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [pauseTimer, setPauseTimer] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.pathname || '/';
  const [params, setParams] = useSearchParams();
  const [guestUserTokenFromUrl, setGuestUserTokenFromUrl] = useState(null);
  const [isGuestWorkflow, setIsGuestWorkflow] = useState(false);

  const isItPubliclyAccessible = route => {
    return (
      /^\/(login|register|forgot-password|authenticate-email|results|authenticate-phone|user-login|doktorlanding|doktor-demographics|atpl-demographics|atpllanding|client-b-demographics|hellohealth-landing|hellohealth-demographics|face-scan-error-page|hellohealth-results|atpl-results)$/.test(
        route,
      ) ||
      /^\/client-results\/[^/]+$/.test(route) || // Matches /client-results/<sessionId>
      /^\/password-reset\/.*$/.test(route)
    );
  };

  const OrglandingUrl = localStorage.getItem('landing_url');

  const isOrgWorkflowRoute = route => {
    return (
      [
        '/doktor-symptom-checker',
        '/results',
        '/doktor-pre-face-scan',
        '/atpl-symptom-checker',
        '/atpl-health-profile',
        '/atpl-pre-face-scan',
        '/results/analysis',
        '/face-scan-generic-error',
        '/hellohealth-landing',
        '/hellohealth-symptom-checker',
        '/hellohealth-pre-face-scan',
        '/hellohealth-health-profile',
      ].includes(route) || /^\/client-results\/[^/]+$/.test(route)
    );
  };

  //check if url contains token & email and navigate accordingly for AIMD flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const email = params.get('email');
    const redirect = params.get('redirect');

    // Check for token and email (guest workflow)
    if (token && email) {
      setGuestUserTokenFromUrl(token);
      setIsGuestWorkflow(true); // Mark as guest workflow
    }
  }, []);

  useEffect(() => {
    if (authTokens) {
      if (['/start', '/demographics', '/pre-face-scan', '/symptom-checker', '/skyn-condition-checker'].includes(from)) {
        axios
          .get(IDENTITY_URL, {
            headers: {
              Authorization: `Bearer ${authTokens.access_token}`,
            },
          })
          .then(res => {})
          .catch(err => {
            if (err.response.status === 403 && err.response.data.detail === 'Anonymous user') {
              logOutUser();
              navigate('/start');
              return;
            }
          });
      }
    }
  }, [authTokens, from]);

  useEffect(() => {
    const sessionId = localStorage.getItem('session-id');
    const isPublicRoute = isItPubliclyAccessible(from);
    const isOrgRoute = isOrgWorkflowRoute(from);

    // Handle org workflow
    if (sessionId) {
      setUserActive({sessionId, type: 'org'});
      if (!isOrgRoute && !isPublicRoute) {
        navigate(OrglandingUrl);
      }
      return;
    }

    // Existing auth flow
    if (!authTokens) {
      setIsLoading(false);
      if (!isPublicRoute) {
        navigate('/start');
        clearStorage();
      }
      return;
    }

    const user = jwtDecode(authTokens.access_token);
    setUserActive(user);
    const isTokenExpired = dayjs.unix(user.exp).diff(dayjs()) < 1;
    if (isTokenExpired) {
      logOutUser();
      return;
    }

    // Updated navigation logic after email authentication
    if (['/authenticate-email'].includes(from)) {
      const nextRoute = getPostAuthNavigation();
      navigate(nextRoute);
    }

    if (['/', '/start'].includes(from)) {
      navigate('/start');
    }

    setIsLoading(false);
  }, [authTokens, from, isGuestWorkflow]);

  const clearStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
  };

  // useEffect(()=>{
  //   if(authTokens){
  //   const storedToken = sessionStorage.getItem('token');
  //   if(isSuccessFullyVerify && storedToken){
  //     navigate('/results')
  //   }else{
  //     navigate('/demographics')
  //   }
  // }
  // },[isSuccessFullyVerify,authTokens])

  //this function will set authToken
  // Update authTokenSetter to handle navigation logic
  const authTokenSetter = (res, isGuest = false) => {
    setAuthTokens(res.data);
    setUserActive(jwtDecode(res.data.access_token));
    localStorage.setItem('authTokens', JSON.stringify(res.data));
    setCanBeAuthenticated(true);
    setIsSuccessfullyVerify(true);

    // Set guest workflow flag if this is a guest login
    if (isGuest) {
      setIsGuestWorkflow(true);
    }
  };

  const getPostAuthNavigation = () => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    const token = params.get('token');

    // If guest workflow (has token) or redirect param points to results
    if (token || redirect === 'results' || isGuestWorkflow) {
      return '/results';
    }

    // Default to demographics for regular users
    return '/demographics';
  };

  //this fn will handle google login & navigate for authenticate-email page for verification
  const googleLoginHandlerNavigateAuthPage = (email, password, params) => {
    const maskedEmail = maskEmail(email);
    const navigateState = {state: {email, password, maskedEmail, params}};
    navigate('/authenticate-email', navigateState);
    return 'redirect';
  };

  const handleNavigationToAuthenticatePage = (email, password, factor) => {
    const maskedEmail = maskEmail(email);
    const navigateState = {state: {email, password, maskedEmail}};
    switch (factor) {
      // case 'phone':
      //   navigate( '/authenticate-phone', navigateState );
      //   break;
      // default:
      case 'email':
        navigate('/authenticate-email', navigateState);
        break;
    }
  };
  // const loginUser = async ( email, password, factor = 'email', code ) => {
  //   try {
  //  //After the 2FA rollback, uncomment this code to restore functionality
  //  console.log("go here");

  //     if ( code === undefined && factor !== 'register' ) {
  //       if ( factor !== 'register' ) {
  //       setIsLoading( true );
  //       // await postAuthCodeSend( email, password, factor );
  //       await postLogin( email, password);
  //       setIsLoading( false );
  //       const navigateState = { state: { email, password } };
  //       switch ( factor ) {
  //         case 'phone':
  //           navigate( '/authenticate-phone', navigateState );
  //           break;
  //         default:
  //         case 'email':
  //           navigate( '/authenticate-email', navigateState );
  //           break;
  //       }
  //       return 'redirect';
  //     }
  //     }
  //     setIsLoading( true );
  //     const res = await TwoFAEmailVerification( email, password, factor, code );
  //     // const res = await postLogin( email, password, factor, code );
  //     console.log(res);
  //     setIsLoading( false );
  //     setAuthTokens( res.data );
  //     setUserActive( jwtDecode( res.data.access_token ) );
  //     localStorage.setItem( 'authTokens', JSON.stringify( res.data ) );
  //     setCanBeAuthenticated( true );
  //     setIsSuccessfullyVerify(true)
  //     return 'ok';
  //   } catch ( error ) {
  //     if ( !error.response ) return;
  //     if ( error.response.status === 401 ) {
  //       setIsLoading( false );
  //       setUnauthorized( true );
  //       setAuthTokens( null );
  //       setUserActive( null );
  //       return 'failed';
  //     }
  //     if ( error.response.status === 403 ) {
  //       const res = await postLogin( email, password, factor, code );

  //       if ( res.status === 401 ) {
  //         setIsLoading( true );
  //         setUnauthorized( true );
  //         setAuthTokens( null );
  //         setUserActive( null );
  //         return 'failed';
  //       }
  //       if ( res.status !== 200 ) return 'failed';

  //       setIsLoading( true );
  //       setAuthTokens( res.data );
  //       setUserActive( jwtDecode( res.data.access_token ) );
  //       localStorage.setItem( 'authTokens', JSON.stringify( res.data ) );
  //       setCanBeAuthenticated( true );
  //       return 'ok';
  //     }
  //     if ( error.response.status === 400 ) {
  //       const authenticateReq = await postAuthenticate( email, password );
  //       if ( authenticateReq.status !== 200 ) return;

  //       const maskedEmail = authenticateReq.data.email;
  //       const maskedPhone = authenticateReq.data.phone_number;

  //       if ( maskedPhone === undefined ) {
  //         postAuthCodeSend( email, password, 'email' );
  //         navigate( '/authenticate-email', {
  //           state: { email, password, maskedEmail, maskedPhone },
  //         } );
  //         setIsLoading( false );
  //         return 'redirect';
  //       }

  //       navigate( '/multifactor', {
  //         state: { email, password, maskedEmail, maskedPhone },
  //       } );
  //       setIsLoading( false );
  //       return 'redirect';
  //     }
  //   }
  // };

  //call to verify new2faEmailVerification
  // const handle2faEmailVerification = async (email, password, factor, code) => {
  //    try{
  //     // setIsLoading( true );
  //     const res = await TwoFAEmailVerification( email, password, factor, code );
  //     console.log(res);
  //     setIsLoading( false );
  //     setAuthTokens( res.data );
  //     setUserActive( jwtDecode( res.data.access_token ) );
  //     localStorage.setItem( 'authTokens', JSON.stringify( res.data ) );
  //     setCanBeAuthenticated( true );
  //     setIsSuccessfullyVerify(true)
  //     return 'ok';
  //    }catch(error){
  //     console.log(error);
  //     // setIsLoading( false );
  //     if ( !error.response ) return 'failed';
  //     //user not available in db
  //     // setIsLoading( false );
  //     setUnauthorized( true );
  //     setAuthTokens( null );
  //     setUserActive( null );
  //     setIsSuccessfullyVerify(false)
  //     if ( error.response.status === 401 ) {
  //       return error.response;
  //     }
  //     //wrong verification code
  //     if ( error.response.status === 403 ) {
  //       return error.response;
  //     }
  //    }
  // }

  //if email verification fail thease will help clear  auth fields

  const clearAutharizationIfError = () => {
    setUnauthorized(true);
    setAuthTokens(null);
    setUserActive(null);
    setIsSuccessfullyVerify(false);
  };

  //call to send new2faEmailVerification code
  const loginUser = async (email, password, factor = 'email') => {
    try {
      if (factor !== 'register') {
        setIsLoading(true);
        //genrate verification code
        await generate2faEmailVerificationCode(email, password);
        setIsLoading(false);
        handleNavigationToAuthenticatePage(email, password, factor);
        return 'redirect';
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setUnauthorized(true);
      setAuthTokens(null);
      setUserActive(null);
      setIsSuccessfullyVerify(false);
      if (error?.response?.status === 401) {
        console.error('Unauthorized access - Please check your credentials.');
      } else {
        console.error('Error during login process:', error);
      }
    }
  };

  const loginGuestUser = async () => {
    const res = await postAnonLogin();

    if (res.status !== 200) return;

    setAuthTokens(res.data);
    setUserActive(jwtDecode(res.data.access_token));
    localStorage.setItem('authTokens', JSON.stringify(res.data));
    setCanBeAuthenticated(true);
    setIsSuccessfullyVerify(true);

    // Also set guest workflow flag for this case
    setIsGuestWorkflow(true);

    return true;
  };

  const setUser = res => {
    setAuthTokens(res.data);
    setUserActive(jwtDecode(res.data.access_token));
    localStorage.setItem('authTokens', JSON.stringify(res.data));
    setCanBeAuthenticated(true);
    return true;
  };

  const setOrgUser = sessionId => {
    localStorage.setItem('session-id', sessionId);
    setUserActive({sessionId, type: 'org'});
    return true;
  };

  const logOutUser = () => {
    const wasOrgUser = userActive?.type === 'org';
    setAuthTokens(null);
    setUserActive(null);
    localStorage.removeItem('authTokens');
    localStorage.removeItem('session-id');
    sessionStorage.clear();
    localStorage.clear();
    if (wasOrgUser) {
      navigate(OrglandingUrl);
    } else {
      navigate('/start');
    }
  };

  const contextData = {
    authTokens,
    setAuthTokens,
    loginUser,
    loginGuestUser,
    setUser,
    setOrgUser,
    logOutUser,
    unauthorized,
    setUnauthorized,
    pauseTimer,
    setPauseTimer,
    userActive,
    setUserActive,
    authTokenSetter,
    googleLoginHandlerNavigateAuthPage,
    clearAutharizationIfError,
    isGuestWorkflow,
    setIsGuestWorkflow,
    getPostAuthNavigation,
  };

  return (
    // <AuthContext.Provider value={ contextData }>
    //   { !isLoading ? children : !authTokens ? '' : <AnimationLoadingScreenWithProgressBar /> }
    // </AuthContext.Provider>
    <AuthContext.Provider value={contextData}>
      {isLoading ? <AnimationLoadingScreenWithProgressBar /> : children}
    </AuthContext.Provider>
  );
};
